import React, { useState, useEffect } from 'react';
import { CreditCard, Phone, Eye, Crown, Calendar, X, AlertCircle, CheckCircle, Gift, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import axios from '../utils/axios.config';
import { API_URL } from '../lib/api-client';
import { Badge } from './ui/badge';
import { formatDistanceToNow } from 'date-fns';
import { useTheme, themes } from './ThemeProvider';

interface PlanStatusNavbarProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToPlans?: () => void;
}

interface SubscriptionData {
  hasSubscription: boolean;
  subscription?: {
    planId: any;
    startDate: string;
    endDate: string;
    status: string;
    contactViewsRemaining: number;
    autoRenew: boolean;
  };
  planDetails?: {
    _id: string;
    name: string;
    description: string;
    duration: number;
    price: number;
    features: any;
  };
  limits?: {
    contactViews: {
      remaining: number;
      total: number;
      used: number;
    };
    videoCalls: {
      remaining: number;
      total: number;
      used: number;
    };
  };
}

export const PlanStatusNavbar: React.FC<PlanStatusNavbarProps> = ({ 
  isOpen, 
  onClose,
  onNavigateToPlans 
}) => {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  useEffect(() => {
    if (isOpen) {
      fetchSubscriptionData();
    }
  }, [isOpen]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      // Fetch subscription data
      const { data } = await axios.get(`${API_URL}/user/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (data.success) {
        setSubscription(data.data);
      }

      // Fetch fresh user data from API instead of localStorage
      const userResponse = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (userResponse.data.success) {
        setUserData(userResponse.data.user);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysRemaining = () => {
    if (!subscription?.subscription?.endDate) return null;
    const endDate = new Date(subscription.subscription.endDate);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusColor = () => {
    const days = getDaysRemaining();
    if (!days || days <= 0) return 'bg-red-100 text-red-800 border-red-200';
    if (days <= 7) return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getStatusIcon = () => {
    const days = getDaysRemaining();
    if (!days || days <= 0) return <AlertCircle className="w-4 h-4" />;
    if (days <= 7) return <AlertCircle className="w-4 h-4" />;
    return <CheckCircle className="w-4 h-4" />;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 md:hidden"
        onClick={onClose}
      />
      
      {/* Slide-in Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-full md:w-[400px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`bg-gradient-to-r ${currentTheme.primary} p-4 text-white flex-shrink-0`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <Crown className="w-6 h-6" />
                <h2 className="text-xl">Plan Status</h2>
              </div>
              <button 
                onClick={onClose}
                className="hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading plan details...</p>
                </div>
              </div>
            ) : !subscription?.hasSubscription ? (
              // No Active Plan
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg text-gray-900 mb-2">No Active Plan</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Subscribe to a plan to unlock premium features and connect with more profiles
                </p>
                <Button 
                  onClick={() => {
                    onClose();
                    if (onNavigateToPlans) {
                      onNavigateToPlans();
                    }
                  }}
                  className={`bg-gradient-to-r ${currentTheme.primary} hover:opacity-90 text-white`}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  View Plans
                </Button>
              </div>
            ) : (
              // Active Plan Details
              <div className="space-y-4">
                {/* Plan Card */}
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Crown className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-lg text-gray-900">
                        {subscription.planDetails?.name || 'Premium Plan'}
                      </h3>
                    </div>
                    <Badge 
                      className={`${getStatusColor()} border flex items-center gap-1`}
                    >
                      {getStatusIcon()}
                      Active
                    </Badge>
                  </div>

                  {subscription.planDetails?.description && (
                    <p className="text-sm text-gray-600 mb-3">
                      {subscription.planDetails.description}
                    </p>
                  )}

                  {/* Expiry Info */}
                  {subscription.subscription?.endDate && (
                    <div className="bg-white rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Expires on
                        </span>
                        <span className="font-medium text-gray-900">
                          {new Date(subscription.subscription.endDate).toLocaleDateString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      
                      {getDaysRemaining() !== null && (
                        <div className="text-xs text-gray-500 text-center pt-1 border-t">
                          {getDaysRemaining()! > 0 
                            ? `${getDaysRemaining()} days remaining`
                            : 'Expired'
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Usage Stats */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <span className="w-1 h-4 bg-purple-600 rounded"></span>
                    Usage Statistics
                  </h4>

                  {/* Contact Views */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Eye className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Contact Views</p>
                          <p className="text-xs text-gray-500">Remaining credits</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">
                          {subscription.limits?.contactViews?.remaining ?? 
                           subscription.subscription?.contactViewsRemaining ?? 
                           0}
                        </p>
                        {subscription.limits?.contactViews?.total && (
                          <p className="text-xs text-gray-500">
                            of {subscription.limits.contactViews.total}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    {subscription.limits?.contactViews && (
                      <div className="space-y-1">
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ 
                              width: `${(subscription.limits.contactViews.remaining / subscription.limits.contactViews.total) * 100}%` 
                            }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 text-right">
                          {subscription.limits.contactViews.used} used
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Video Calls */}
                  {subscription.limits?.videoCalls && (
                    <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <Phone className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Video Calls</p>
                            <p className="text-xs text-gray-500">Remaining credits</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">
                            {subscription.limits.videoCalls.remaining}
                          </p>
                          {subscription.limits.videoCalls.total && (
                            <p className="text-xs text-gray-500">
                              of {subscription.limits.videoCalls.total}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="space-y-1">
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all"
                            style={{ 
                              width: `${(subscription.limits.videoCalls.remaining / subscription.limits.videoCalls.total) * 100}%` 
                            }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 text-right">
                          {subscription.limits.videoCalls.used} used
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Booster Credits */}
                  {userData?.boostCredits > 0 && userData?.profileBoostActive && (
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Sparkles className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">Profile Booster Credits</p>
                            <p className="text-xs text-purple-600">Active boost pack</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-purple-600">
                            {userData.boostCredits}
                          </p>
                          <p className="text-xs text-purple-500">
                            {userData.boostMultiplier}x boost
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Premium Plans (Addons) */}
                {userData?.premiumPlans && userData.premiumPlans.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <span className="w-1 h-4 bg-indigo-600 rounded"></span>
                      Premium Add-ons
                    </h4>
                    {userData.premiumPlans.map((plan: any, index: number) => {
                      // Only show non-subscription plans
                      if (plan.planType === 'subscription') return null;
                      
                      return (
                        <div key={plan._id || index} className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-200">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{plan.planName}</p>
                              <p className="text-xs text-indigo-600">
                                {plan.addonType === 'profile_booster' && 'Profile Booster Pack'}
                                {plan.addonType === 'video_calls' && 'Video Call Credits'}
                                {plan.addonType === 'contact_views' && 'Contact View Credits'}
                              </p>
                            </div>
                            {plan.credits !== undefined && (
                              <div className="text-right">
                                <p className="text-xl font-bold text-indigo-600">{plan.credits}</p>
                                <p className="text-xs text-indigo-500">credits</p>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            Purchased {new Date(plan.purchaseDate).toLocaleDateString('en-US', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Rewards Credits Section */}
                {userData?.rewardCredits && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <span className="w-1 h-4 bg-amber-600 rounded"></span>
                      Rewards Program
                    </h4>

                    {/* Show all reward types */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Contact Views Rewards */}
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-3 border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Eye className="w-4 h-4 text-amber-600" />
                          <p className="text-xs font-medium text-gray-900">Contact Views</p>
                        </div>
                        <p className="text-2xl font-bold text-amber-600">
                          {userData.rewardCredits.contactViews || 0}
                        </p>
                        <p className="text-xs text-amber-500">Credits</p>
                      </div>

                      {/* Interests Rewards */}
                      <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-lg p-3 border border-pink-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Gift className="w-4 h-4 text-pink-600" />
                          <p className="text-xs font-medium text-gray-900">Interests</p>
                        </div>
                        <p className="text-2xl font-bold text-pink-600">
                          {userData.rewardCredits.interests || 0}
                        </p>
                        <p className="text-xs text-pink-500">Credits</p>
                      </div>

                      {/* Super Interests Rewards */}
                      <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg p-3 border border-purple-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          <p className="text-xs font-medium text-gray-900">Super Interests</p>
                        </div>
                        <p className="text-2xl font-bold text-purple-600">
                          {userData.rewardCredits.superInterests || 0}
                        </p>
                        <p className="text-xs text-purple-500">Credits</p>
                      </div>

                      {/* Video Call Credits Rewards */}
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Phone className="w-4 h-4 text-green-600" />
                          <p className="text-xs font-medium text-gray-900">Video Calls</p>
                        </div>
                        <p className="text-2xl font-bold text-green-600">
                          {userData.rewardCredits.videoCallCredits || 0}
                        </p>
                        <p className="text-xs text-green-500">Credits</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="space-y-2 pt-2">
                  <Button 
                    onClick={() => {
                      onClose();
                      if (onNavigateToPlans) {
                        onNavigateToPlans();
                      }
                    }}
                    className={`w-full bg-gradient-to-r ${currentTheme.primary} hover:opacity-90 text-white`}
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>

                  {getDaysRemaining() !== null && getDaysRemaining()! <= 10 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-800">
                          <p className="font-medium mb-1">Plan expiring soon!</p>
                          <p>Renew now to avoid losing access to premium features.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};